<template>
    <hts-drawer
        v-model="visible"
        :custom="['header', 'footer']"
        :title="$t('labels.addDocument')"
        :submit="visibleSubmit"
        :rules="rules.addDocumentVehicle"
        :undo-enable="true"
        @close="closeOverSide"
        @undo="resetField()">
        <div class="hts-vehicles-ignore-add-vehicle">
            <div class="hts-vehicles-ignore-add-vehicle__container-body">
                <hts-card :title="$t('labels.generalInformation')">
                    <div
                        class="grid gap-4"
                        :class="columnClass">
                        <hts-form-select
                            v-model="dataFormAddDocument.type"
                            :label="$t('labels.documentType')"
                            required
                            :items="listTypeDocument"
                            key-id="key"
                            key-label="value"
                            :color="color"
                            name="documentType"
                            prop="documentType"
                            @change="(data: any) => handleChangeType(data)" />
                        <hts-form-input
                            v-model="dataFormAddDocument.fileName"
                            :label="$t('labels.documentFileName')"
                            :color="color"
                            name="documentFileName"
                            prop="documentFileName" />
                        <hts-form-input
                            v-if="dataFormAddDocument.type === listTypeDocument[0].key"
                            v-model="dataFormAddDocument.insurancePrice"
                            required
                            :label="$t('labels.insurancePriceByYear')"
                            :color="color"
                            type="number"
                            name="insurancePriceByYear"
                            prop="insurancePriceByYear" />
                        <hts-form-date
                            v-if="dataFormAddDocument.type === listTypeDocument[0].key"
                            v-model="dataFormAddDocument.expiredDate"
                            :label="$t('labels.expiredDate')"
                            :color="color"
                            required
                            name="expiredDate"
                            prop="expiredDate" />
                        <hts-form-file
                            ref="file"
                            v-model="fileDocument"
                            :required="true"
                            type="file"
                            :label="$t('labels.document')"
                            name="document"
                            prop="document" />
                        <hts-form-date
                            v-if="dataFormAddDocument.type === listTypeDocument[1].key"
                            v-model="dataFormAddDocument.expiredDate"
                            required
                            :label="$t('labels.expiredDate')"
                            :color="color"
                            name="expiredDate"
                            prop="expiredDate" />
                        <hts-form-input
                            v-model="dataFormAddDocument.description"
                            :label="$t('labels.description')"
                            :color="color"
                            name="description"
                            prop="description" />
                    </div>
                </hts-card>
            </div>
        </div>
        <template #footer>
            <div class="flex flex-row gap-5">
                <hts-form-button
                    icon-name="hi-check-icon"
                    icon-position="right"
                    color="primary"
                    type="submit"
                    :loading="creating">
                    {{ $t('labels.save') }}
                </hts-form-button>
                <hts-form-button
                    icon-name="hi-x-mark-icon"
                    icon-position="right"
                    color="error"
                    @click="visible = false">
                    {{ $t('labels.cancel') }}
                </hts-form-button>
            </div>
        </template>
        <hts-modal
            :show="showConfirmAddDocument"
            :title="$t('labels.confirmCreateDocument')"
            type="confirm"
            :content="$t('labels.contentConfirmExpiredDateDocument')"
            @hide="showConfirmAddDocument = false"
            @confirm="handleSubmitFormAddDocument" />
    </hts-drawer>
</template>

<script lang="ts" setup>
import form, { IVehicleDocument } from '../imports/form'
const { rules } = form

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 2
  },
  type: {
    type: String,
    default: ''
  }
})

const emits = defineEmits(['refreshDataTables'])

const _initForm = {
  document: '',
  fileName: '',
  type: '',
  privacy: '',
  description: '',
  expiredDate: '',
  insurancePrice: null
}
const dataFormAddDocument = reactive<IVehicleDocument>({ ..._initForm })
const fileDocument = ref<File | string>('')

const visible = ref(false)
const creating = ref(false)
const idVehicle = ref('')

const openAddDocument = (id: any) => {
  if (!id) {
    return
  }

  idVehicle.value = id
  visible.value = true
}
defineExpose({
  openAddDocument
})

const resetField = () => {
  fileDocument.value = ''
  Object.assign(dataFormAddDocument, { ..._initForm })
}
const listTypeDocument = [
  {
    key: 'INSURANCE',
    value: $t('labels.vehicleModule.insurance')
  },
  {
    key: 'REGISTRY',
    value: $t('labels.registry')
  },
  {
    key: 'OTHER',
    value: $t('labels.other')
  }
]
const showConfirmAddDocument = ref(false)
const visibleSubmit = () => {
  if (
    dataFormAddDocument.type &&
    ['REGISTRY', 'INSURANCE'].includes(dataFormAddDocument.type) &&
    dataFormAddDocument.expiredDate &&
    new Date(dataFormAddDocument.expiredDate + ' 23:59:59').getTime() < new Date().getTime()
  ) {
    showConfirmAddDocument.value = true
  } else {
    handleSubmitFormAddDocument()
  }
}

const handleChangeType = (data: any) => {
  if (data.length > 0) {
    dataFormAddDocument.type = data[0].key
  }
}
const handleSubmitFormAddDocument = () => {
  if (creating.value === true) {
    process.client && $toast().warning($t('labels.loading'))
  } else {
    const dataForm = new FormData()
    dataForm.append('document', fileDocument.value)
    dataForm.append('fileName', dataFormAddDocument.fileName || '')
    dataForm.append(props.type, idVehicle.value)
    dataForm.append('type', dataFormAddDocument.type || '')
    dataForm.append('privacy', dataFormAddDocument.privacy || '')
    dataForm.append('description', dataFormAddDocument.description || '')
    dataForm.append('expiredDate', dataFormAddDocument.expiredDate || '')
    dataForm.append('insurancePrice', dataFormAddDocument.insurancePrice as unknown as string)
    const api = $urlPartner.vehicle.vehicle.uploadDocument
    creating.value = true
    $api(api, { body: dataForm })
      .then((data) => {
        const resData: any = data?.data.value
        if (resData.success) {
          process.client && $toast().success($t('notifications.addSuccessDocument'))
          visible.value = false
        }
        creating.value = false
        resetField()
        emits('refreshDataTables')
      })
      .catch((e: any) => {
        creating.value = false
      })
    showConfirmAddDocument.value = false
  }
}

const columnClass = computed(() => {
  if ($lodash.inRange(props.column, 1, 12)) {
    return `grid-cols-${props.column}`
  }
  return 'grid-cols-1'
})

const closeOverSide = () => {
  visible.value = false
  resetField()
}
</script>
